<template>
  <main class="settings">
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave"
    />
    <div class="settings__container">
      <h1 class="settings__title">Настройки программы</h1>
      <div class="settings__description">
        Чтобы начать работу в системе, выполните следующие шаги
      </div>
      <div class="settings__form">
        <form-settings-editing
          ref="formSetting"
          v-bind:show-cashbox-settings="isShowCashboxSettings"
          v-bind:submit-trigger="submitTrigger"
          v-bind:violation-causes="violationCauses"
          v-bind="generalProps"
          v-if="hasSettings"
          v-on:submit="onAllSubmit"
          v-on:submit-by-trigger="onSubmitSettings"
          v-on:change="onChangeSettings"
        />
      </div>
      <patient-section-control
        v-model="openPanels"
        ref="branches-panels"
      >
        <patient-section
          class="settings__branch-form"
          v-bind:label="branchNamesById[mainBranchId]"
          v-bind:description="mainBranchDescription"
        >
          <template
            v-if="renamingBranch === mainBranchId"
            v-slot:label
          >
            <form
              v-on:click.stop
              v-on:keyup.esc="onBranchRenamingToggle(mainBranchId)"
              v-on:submit.prevent="onBranchRenamingToggle(mainBranchId)"
            >
              <input-text
                v-bind:ref="`branch${mainBranchId}NameInputRef`"
                v-bind:value="branchNamesById[mainBranchId]"
                v-on:input="onBranchRenamingInput(mainBranchId, $event)"
              />
            </form>
          </template>
          <template
            v-if="mainBranchRenamable"
            v-slot:after-label
          >
            <button
              class="branch-rename"
              type="button"
              v-on:click.stop="onBranchRenamingToggle(mainBranchId)"
            >Переименовать</button>
          </template>
          <template
            v-if="mainBranchMark"
            v-slot:after-header
          >
            <em class="settings__branch-mark">{{ mainBranchMark }}</em>
          </template>
          <form-organization-branch-editing
            v-bind:cities="cityList"
            v-bind:settings-options="mainBranchSettingsOptions"
            v-bind:show-cashbox-settings="isShowCashboxSettings"
            v-bind:submit-trigger="submitTrigger"
            v-bind:violation-causes="violationCauses"
            v-bind="mainBranchProps"
            v-if="hasSettings"
            v-on:change="onChangeBranch"
            v-on:submit="onAllSubmit"
            v-on:submit-by-trigger="onSubmitBranch(mainBranchId, ...arguments)"
          />
        </patient-section>
        <patient-section
          class="settings__branch-form"
          v-bind:description="description"
          v-bind:key="`additional-branches:${id}`"
          v-bind:label="branchNamesById[id]"
          v-for="({
            description,
            id,
            isRenamable,
            props,
            violations,
          }) in additionalBranchesWithViolations"
        >
          <template
            v-if="renamingBranch === id"
            v-slot:label
          >
            <form
              v-on:click.stop
              v-on:keyup.esc="onBranchRenamingToggle(id)"
              v-on:submit.prevent="onBranchRenamingToggle(id)"
            >
              <input-text
                v-bind:ref="`branch${id}NameInputRef`"
                v-bind:value="branchNamesById[id]"
                v-on:input="onBranchRenamingInput(id, $event)"
              />
            </form>
          </template>
          <template
            v-if="isRenamable"
            v-slot:after-label
          >
            <button
              class="branch-rename"
              type="button"
              v-on:click.stop="onBranchRenamingToggle(id)"
            >Переименовать</button>
          </template>
          <form-organization-branch-editing
            v-bind:cities="cityList"
            v-bind:settings-options="mainBranchSettingsOptions"
            v-bind:show-cashbox-settings="isShowCashboxSettings"
            v-bind:submit-trigger="submitTrigger"
            v-bind:violation-causes="violations"
            v-bind="props"
            v-on:change="onChangeBranch"
            v-on:submit="onAllSubmit"
            v-on:submit-by-trigger="onSubmitBranch(id, ...arguments)"
          />
        </patient-section>
      </patient-section-control>
      <div class="settings__text">
        Подробные инструкции по работе c
        <button
          type='button'
          @click='onHelpButtonClick'
          class="settings__base-of-knowledge-button"
        >
          системой
        </button>
      </div>
    </div>
    <next-step-setting-instruction
      class="settings__next-step"
      v-bind:initial-steps="availableInstructionSteps"
      v-bind:current-route-name="$route.name"
    >
      <div class="settings-actions">
        <button
          v-if="hasEditSetting"
          class="settings-actions__button"
          type="submit"
          v-on:click="onAllSubmit"
        >Сохранить</button>
        <button
          class="settings-actions__inactive-button settings-actions__invite-to-branch"
          type="button"
          v-if="hasInviteToBranch"
          v-on:click="onInviteToBranchOpen"
        >Пригласить в сеть</button>
        <button
          class="settings-actions__inactive-button settings-actions__join-to-branch"
          type="button"
          v-if="hasJoinToBranch"
          v-on:click="onJoinToBranchOpen"
        >Присоединиться к сети</button>
        <button
          class="settings-actions__button"
          type="button"
          v-if="hasAddBranch"
          v-on:click="onAddBranchOpen"
        >Добавить филиал</button>
      </div>
    </next-step-setting-instruction>
    <modal-dialog
      title="Шаг 1. Для подключения филиала создайте ключ доступа"
      v-bind:with-cross="true"
      v-model="isShownInviteToBranchDialog"
    >
      <button
        class="invite-to-branch-button"
        type="button"
        v-on:click="onInviteToBranchCreateKey"
      >Создать ключ</button>
    </modal-dialog>
    <modal-dialog
      title="Шаг 2. Передайте ключ доверенному лицу филиала, который подключаете"
      v-bind:with-cross="true"
      v-bind:value="hasInvitationKeyToBranch"
      v-on:input="onInvitationKeyDialogInput"
    >
      <strong>Не закрывайте это окно, иначе ключ станет неактивным</strong>
      <copy-line
        v-bind:value="invitationKeyToBranch"
        v-on:clipboard="onInvitationKeyCopied" />
      <the-timer
        v-bind:actual-ms="invitationKeyNowTimestamp"
        v-bind:finish-ms="invitationKeyExpiresTimestamp"
        v-on:timeout="onInvitationKeyExpired" />
    </modal-dialog>
    <modal-dialog
      title="Шаг 3. Если клиника не успела подключиться, создайте ключ повторно"
      v-bind:with-cross="true"
      v-bind:value="hasExpiredInvitationKey"
      v-on:input="onInvitationKeyDialogInput"
    >
      <button
        class="invite-to-branch-button"
        type="button"
        v-on:click="onInviteToBranchCreateKey"
      >Запросить повторно</button>
    </modal-dialog>
    <modal-dialog
      content-class="join-to-branch-dialog"
      max-width="720px"
      title="Для подключения к основному филиалу, введите ключ доступа. Запросить ключ можно у владельца основного филиала"
      v-bind:with-cross="true"
      v-model="isShownJoinToBranchDialog"
    >
      <form
        class="join-to-branch-form"
        v-on:submit.prevent="onJoinToBranchSubmit"
      >
        <input-secret
          class="join-to-branch-dialog__input-secret"
          label="Ключ аутентификации"
          v-bind:max-length="12"
          v-model="joinToBranchKey" />
        <button
          class="join-to-branch-form__submit"
          type="submit"
        >Подключиться</button>
      </form>
    </modal-dialog>
    <modal-dialog
      title="Филиал успешно подключен!"
      v-model="isShownJoinToBranchSuccessDialog"
    >
      <button
        class="invite-to-branch-button"
        type="button"
        v-on:click="onJoinedToBranchClick"
      >Ок</button>
    </modal-dialog>
    <modal-dialog
      v-bind:title="invitationKeyTitle"
      v-model="hasInvitationKeyTitleDialog"
    >
      <button
        class="invite-to-branch-button"
        type="button"
        v-on:click="onInvitationKeyDialogOk"
      >ОК</button>
    </modal-dialog>
    <side-slide-transition>
      <confirm-side-dialog
        label="Добавить филиал"
        v-if="isShownAddBranchDialog"
        v-on:cancel="onAddBranchCancel"
      >
        <form-organization-branch-adding
          v-bind:cities="cityList"
          v-bind:disabled="false"
          v-bind:settings-options="mainBranchSettingsOptions"
          v-bind:violation-causes="addBranchViolationCauses"
          v-bind:main-branch-props="mainBranchProps"
          is-create-branch
          v-on:submit="onAddBranchSubmit"
        />
      </confirm-side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import CopyLine from '@/components/CopyLine.vue';
import FormOrganizationBranchEditing from '@/components/FormOrganizationBranchEditing.vue';
import FormOrganizationBranchAdding from '@/components/FormOrganizationBranchAdding.vue';
import FormSettingsEditing from '@/components/FormSettingsEditing.vue';
import InputSecret from '@/components/InputSecret.vue';
import InputText from '@/components/InputText.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import NextStepSettingInstruction from '@/components/NextStepSettingInstruction.vue';
import PatientSection, { PatientSectionControl } from '@/components/PatientSection.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import TheTimer from '@/components/TheTimer.vue';
import focusMixin from '@/mixins/focusMixin';
import notifierMixin from '@/mixins/notifierMixin';
import onBoardingMixin from '@/mixins/onBoardingMixin';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import settingInstructionMixin from '@/mixins/settingInstructionMixin';
import { EditClinicSettingsRequest } from '@/storage/gateways/SettingGateway';
import { Maybe } from 'monet';

/**
 * @description Отображатель данных филиала в объект.
 */
const branchMapper = (
  clinicAddress,
  clinicAllowedActions,
  clinicCity,
  clinicINN,
  clinicBIN,
  clinicKPP,
  directorName,
  directorPatronymic,
  directorSurname,
  egiszConfidentialityId,
  id,
  isTradeAcquiringEnabled,
  legalName,
  licenseIssuedDate,
  licenseIssuer,
  licenseSerialAndNumber,
  phoneNumber,
  defaultVatTitle,
  defaultVatValue,
  snoTitle,
  snoValue,
  taxAuthorityCode,
  clinicOGRN,
  clinicRightFormRegistration,
  tags,
  title,
) => ({
  clinicAddress,
  clinicAllowedActions,
  clinicCity,
  clinicINN,
  clinicBIN,
  clinicKPP,
  description: tags
    .flatMap((value) => (value === 'you-are-here' ? ['Вы здесь'] : []))
    .join(', '),
  directorName,
  directorPatronymic,
  directorSurname,
  egiszConfidentialityId,
  id,
  isAuthenticated: tags.includes('you-are-here'),
  isRenamable: clinicAllowedActions.includes('rename-branch'),
  isTradeAcquiringEnabled,
  legalName,
  licenseIssuedDate,
  licenseIssuer,
  licenseSerialAndNumber,
  mark: tags
    .flatMap((value) => (value === 'main-branch' ? ['Основной филиал'] : []))
    .join(', '),
  phoneNumber,
  defaultVatTitle,
  defaultVatValue,
  snoTitle,
  snoValue,
  taxAuthorityCode,
  clinicOGRN,
  clinicRightFormRegistration,
  title,
});

/**
 * @description Отображатель общих данных в объект.
 */
const generalMapper = (
  clinicName,
  logoUrl,
) => ({
  clinicName,
  logo: (logoUrl !== '' ? { name: 'Скачать', url: logoUrl } : null),
});

const collectBranchEditingProps = (branch) => ({
  branchName: branch.title,
  disabled: branch.clinicAllowedActions.includes('edit-settings') !== true,
  initialClinicAddress: branch.clinicAddress,
  initialClinicCity: branch.clinicCity,
  initialClinicINN: branch.clinicINN,
  initialClinicKPP: branch.clinicKPP,
  initialConfidentialityLevelId: branch.egiszConfidentialityId,
  initialDirectorName: branch.directorName,
  initialDirectorPatronymic: branch.directorPatronymic,
  initialDirectorSurname: branch.directorSurname,
  initialIsTradeAcquiringEnabled: branch.isTradeAcquiringEnabled,
  initialLegalName: branch.legalName,
  initialLicenseIssuedDate: branch.licenseIssuedDate,
  initialLicenseIssuer: branch.licenseIssuer,
  initialLicenseSerialAndNumber: branch.licenseSerialAndNumber,
  initialPhoneNumber: branch.phoneNumber,
  initialSno: branch.snoValue,
  initialVat: branch.defaultVatValue,
  initialLicenseNumber: branch.licenseNumber,
  initialTaxAuthorityCode: branch.taxAuthorityCode,
  initialClinicOGRN: branch.clinicOGRN,
  initialClinicRightFormRegistration: branch.clinicRightFormRegistration,
  initialClinicBIN: branch.clinicBIN,
});

/**
 * @description Название полей ошибок.
 * @type {string[]}
 */
const nameViolationFields = [
  'clinic_city',
  'clinic_address',
  'clinic_inn',
  'clinic_kpp',
  'clinic_ogrn',
  'director_name',
  'director_patronymic',
  'director_surname',
  'egisz_confidentiality',
  'legal_name',
  'license_issued_date',
  'license_issuer',
  'license_serial_and_number',
  'phone_number',
  'clinic_right_form_registration',
  'tax_authority_code',
];

/**
 * @description Страница базовых настроек системы.
 */
export default {
  beforeDestroy() {
    if (this.invitationKeyNowIntervalId !== null) {
      clearInterval(this.invitationKeyNowIntervalId);
    }
  },
  components: {
    ConfirmSideDialog,
    CopyLine,
    FormOrganizationBranchAdding,
    FormOrganizationBranchEditing,
    FormSettingsEditing,
    InputSecret,
    InputText,
    ModalDialog,
    ModalDialogPreventLeave,
    NextStepSettingInstruction,
    PatientSection,
    PatientSectionControl,
    SideSlideTransition,
    TheTimer,
  },
  computed: {
    /**
     * @description Данные дополнительных филиалов с нарушениями в полях.
     */
    additionalBranchesWithViolations() {
      return this.additionalBranches.map((branch) => ({
        ...branch,
        violations: this.violationCausesForBranches[branch.id] || {},
      }));
    },
    /**
     * @description Идентификатор аутентифицированного филиала.
     */
    authBranchId() {
      const forAdditionals = () => {
        const additionals = this.additionalBranches.filter(
          ({ isAuthenticated }) => isAuthenticated,
        );
        return additionals.length !== 0 ? Maybe.Some(additionals[0]) : Maybe.None();
      };
      const forMain = this.mainBranch.bind(
        (branch) => (branch.isAuthenticated ? Maybe.Some(branch) : Maybe.None()),
      );
      return forMain.catchMap(forAdditionals).fold(null)(({ id }) => id);
    },
    /**
     * @description Доступно действие добавления филиала.
     */
    hasAddBranch() {
      return this.actions.includes('add-branch');
    },
    /**
     * @description Свойства общих данных.
     */
    generalProps() {
      return this.general.fold({})((general) => ({
        initialClinicName: general.clinicName,
        initialLogo: general.logo,
      }));
    },
    /**
     * @description Может редактировать настройки.
     * @type {boolean}
     */
    hasEditSetting() {
      return this.actions.includes('edit-settings');
    },
    /**
     * @description Имеет истекший ключ приглашения в сеть.
     */
    hasExpiredInvitationKey() {
      return this.invitationKeyExpiresTimestamp !== 0
        && this.invitationKeyToBranch === '';
    },
    /**
     * @description Имеет показ диалога по ключу приглашения в сеть.
     */
    hasInvitationKeyTitleDialog: {
      get() {
        return this.invitationKeyTitle !== '';
      },
      set(value) {
        if (Boolean(value) !== true) {
          this.invitationKeyTitle = '';
        }
      },
    },
    /**
     * @description Имеет ключ приглашения в сеть.
     */
    hasInvitationKeyToBranch() {
      return this.invitationKeyToBranch !== '';
    },
    /**
     * @description Доступно действие приглашения в сеть.
     */
    hasInviteToBranch() {
      return this.actions.includes('invite-to-branch');
    },
    /**
     * @description Доступно действие присоединения к сети.
     */
    hasJoinToBranch() {
      return this.actions.includes('join-to-branch');
    },
    /**
     * @description Доступна филиальная система.
     * @returns {boolean}
     */
    hasBranchSystem() {
      return !!this.$store.getters.isSectionAvailable('branch-system');
    },
    isShowCashboxSettings() {
      return this.actions.includes('edit-cashbox');
    },
    /**
     * @description Готов к отправке.
     */
    hasReadySubmit() {
      const checkReady = (value) => this.submitTrigger === value;
      return this.submitTrigger
        && checkReady(this.submitTriggerOfSettings)
        && Object.values(this.submitTriggersForBranches).every(checkReady);
    },
    /**
     * @description Имеются настройки.
     * @type {boolean}
     */
    hasSettings() {
      return this.general.isSome();
    },
    /**
     * @description Описание основного филиала.
     */
    mainBranchDescription() {
      if (this.additionalBranches.length !== 0) {
        return this.mainBranch.fold('')(({ description }) => description);
      }
      return '';
    },
    /**
     * @description Идентификатор основного филиала.
     */
    mainBranchId() {
      return this.mainBranch.fold('default')(({ id }) => id);
    },
    /**
     * @description Надпись метка основного филиала.
     */
    mainBranchMark() {
      return this.mainBranch.fold('')(({ mark }) => mark);
    },
    /**
     * @description Свойства основного филиала.
     */
    mainBranchProps() {
      return this.mainBranch.fold({})(collectBranchEditingProps);
    },
    /**
     * @description Основной филиал подлежит переименованию.
     */
    mainBranchRenamable() {
      return this.mainBranch.fold(false)(
        ({ clinicAllowedActions }) => clinicAllowedActions.includes('rename-branch'),
      );
    },
    /**
     * @description Опции.
     * @type {{vatOptions:any[],snoOptions:any[]}
     */
    mainBranchSettingsOptions() {
      const getFallbackOptions = () => {
        const options = {
          egiszConfidentialities: [],
          snoOptions: [],
          vatOptions: [],
          rightFormRegistrationsOptions: [],
        };
        return this.mainBranch.fold(options)((mainBranch) => {
          if (mainBranch.snoValue !== '' && mainBranch.defaultVatValue !== '') {
            const initialSnoOption = {
              title: mainBranch.snoTitle,
              value: mainBranch.snoValue,
            };
            const initialVatOption = {
              title: mainBranch.defaultVatTitle,
              value: mainBranch.defaultVatValue,
            };
            options.snoOptions.push(initialSnoOption);
            options.vatOptions.push(initialVatOption);
          }
          return options;
        });
      };
      return this.clinicSettingsOptions.cata(getFallbackOptions, (value) => value);
    },
    /**
     * @description Заголовок основного филиала.
     */
    mainBranchName() {
      return this.mainBranch.fold('Основной филиал')(({ title }) => title);
    },
    /**
     * @description Предотвращать уход со страницы.
     * @type {boolean}
     */
    needPreventLeave: {
      get() {
        return (this.submitTrigger === this.submitTriggerOfSettings)
          && (this.hasChangedBranch || this.hasChangedSettings);
      },
    },
  },
  created() {
    this.$cityGateway.getAll().then((items) => { this.cityList = items; });
    this.fetchSettingsOptions();
    this.fetchSettings();
    this.$storageChangeOn(this)('settings', () => { this.fetchSettings(); });
  },
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      /**
       * @description Нарушения в форме добавления филиала.
       * @type {object}
       */
      addBranchViolationCauses: {},
      /**
       * @description Дополнительные филиалы.
       * @type {(object[])}
       */
      additionalBranches: [],
      /**
       * @description Названия филиалов по их идентификатору.
       * @type {object}
       */
      branchNamesById: {
        default: 'Основной филиал',
      },
      /**
       * @description Список городов.
       * @type {any[]}
       */
      cityList: [],
      /**
       * @description Опции настроек клиники.
       * @type {Maybe}
       */
      clinicSettingsOptions: Maybe.none(),
      /**
       * @description Общие данные.
       * @type {Maybe}
       */
      general: Maybe.None(),
      /**
       * @description Имеет изменения по основному филиалу.
       * @type {boolean}
       */
      hasChangedBranch: false,
      /**
       * @description Имеет изменения по основным данным.
       * @type {boolean}
       */
      hasChangedSettings: false,
      /**
       * @description Неактивные действия.
       * @type {({id: string, title: string}[])}
       */
      inactiveActions: [],
      /**
       * @description Время истечения ключа приглашения в сеть.
       * @type {number}
       */
      invitationKeyExpiresTimestamp: 0,
      /**
       * @description Время очередной проверки ключа приглашения в сеть.
       * @type {number}
       */
      invitationKeyNextCheckMs: 0,
      /**
       * @description Идентификатор таймера текущего времени.
       * @type {(number|null)}
       */
      invitationKeyNowIntervalId: null,
      /**
       * @description Текущее время.
       * @type {number}
       */
      invitationKeyNowTimestamp: 0,
      /**
       * @description Заголовок ключа приглашения.
       * @type {string}
       */
      invitationKeyTitle: '',
      /**
       * @description Ключ приглашения в сеть.
       * @type {string}
       */
      invitationKeyToBranch: '',
      /**
       * @description Ожидается запрос.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Показать диалог добавления филиала.
       * @type {boolean}
       */
      isShownAddBranchDialog: false,
      /**
       * @description Показать диалог приглашения в сеть.
       * @type {boolean}
       */
      isShownInviteToBranchDialog: false,
      /**
       * @description Показать диалог присоединения к сети.
       * @type {boolean}
       */
      isShownJoinToBranchDialog: false,
      /**
       * @description Показать диалог успешного подключения к сети.
       * @type {boolean}
       */
      isShownJoinToBranchSuccessDialog: false,
      /**
       * @description Ключ присоединения к сети.
       * @type {string}
       */
      joinToBranchKey: '',
      /**
       * @description Данные основного филиала.
       * @type {Maybe}
       */
      mainBranch: Maybe.None(),
      /**
       * @description Нужно акцентировать внимание на инструкции.
       * @type {boolean}
       */
      needsAccentToInstruction: false,
      /**
       * @description Идентификатор переименования филиала.
       * @type {(string|null)}
       */
      renamingBranch: null,
      /**
       * @description Настройки.
       * @type {Maybe}
       */
      settings: Maybe.none(),
      /**
       * @description Данные отправки настроек.
       * @type {(object|null)}
       */
      submitRequestOfSettings: null,
      /**
       * @description Данные отправляемых настроек по филиалам.
       * @type {object}
       */
      submitRequestsForBranches: {},
      /**
       * @description Триггер отправки.
       * @type {number}
       */
      submitTrigger: 0,
      /**
       * @description Триггер отправки настроек.
       * @type {number}
       */
      submitTriggerOfSettings: 0,
      /**
       * @description Триггеры отправки настроек филиалов.
       * @type {object}
       */
      submitTriggersForBranches: {},
      /**
       * @description Список нарушений ограничений.
       * @type {any}
       */
      violationCauses: {},
      /**
       * @description Список нарушений по филиалам.
       * @type {object}
       */
      violationCausesForBranches: {},
      /**
       * @description Раскрытые панели.
       */
      openPanels: [],
    };
  },
  methods: {
    /**
     * @description Имеются ошибки по филиалам
     * @returns {boolean}
     */
    checkBranchesViolations() {
      const hasViolations = nameViolationFields.some(
        (field) => Object.hasOwn(this.violationCauses, field)
          || Object.values(this.violationCausesForBranches).flatMap(Object.keys).includes(field),
      );
      if (hasViolations) {
        this.openPanels = [0, ...this.additionalBranches.map((_, index) => index + 1)];
        setTimeout(() => this.onScrollToPanels(), 300);
      }
      return hasViolations;
    },
    /**
     * @description Обработать отмену диалога добавления филиала.
     * @returns {void}
     */
    onAddBranchCancel() {
      this.isShownAddBranchDialog = false;
    },
    /**
     * @description Обработать нажатие на ссылку перехода к базе знаний.
     * @returns {void}
     */
    onHelpButtonClick() {
      window.open('https://mis-help.32top.kz/instructions', '_blank', 'noopener noreferrer');
    },
    /**
     * @description Обработать открытие диалога добавления филиала.
     * @returns {void}
     */
    onAddBranchOpen() {
      this.addBranchViolationCauses = {};
      this.isShownAddBranchDialog = true;
    },
    /**
     * @description Обработать отправку данных нового филиала.
     * @param {object} data Данные филиала.
     * @returns {void}
     */
    onAddBranchSubmit(data) {
      if (this.isAwaitRequest) {
        return;
      }
      const request = new EditClinicSettingsRequest(data);
      this.isAwaitRequest = true;
      this
        .$settingGateway
        .addBranch(request)
        .then((result) => {
          result.forEachLeft(({ causes, title }) => {
            this.notifyFail(title);
            const failurePairs = causes.map((cause) => [cause.source, cause.details]);
            this.addBranchViolationCauses = Object.fromEntries(failurePairs);
            this.isAwaitRequest = false;
          });
          result.forEach(({ title }) => {
            this.isShownAddBranchDialog = false;
            this.notifySuccess(title);
            this.isAwaitRequest = false;
          });
        });
    },
    onAllSubmit() {
      this.submitTrigger = Date.now();
    },
    /**
     * @description Обработать ввод названия филиала.
     * @param {string} branch Идентификатор филиала.
     * @param {string} value  Значение.
     * @returns {void}
     */
    onBranchRenamingInput(branch, value) {
      this.$set(this.branchNamesById, branch, value);
    },
    /**
     * @description Обработать нажатие на переименования филиала.
     * @param {string} branch Идентификатор филиала.
     * @returns {void}
     */
    onBranchRenamingToggle(branch) {
      const refName = `branch${branch}NameInputRef`;
      this.renamingBranch = (this.renamingBranch === branch ? null : branch);
      if (this.renamingBranch !== null) {
        this.$nextTick(() => {
          if (this.mainBranchId === branch) {
            this.$refs[refName].$el.focus();
          } else {
            this.$refs[refName].forEach((vm) => { vm.$el.focus(); });
          }
        });
      }
    },
    /**
     * @description Обработать проверку ключа приглашения.
     * @param {string} value Значение ключа.
     * @returns {void}
     */
    onInvitationKeyCheck(value) {
      if (this.invitationKeyNextCheckMs === 0) {
        return;
      }
      if (Date.now() < this.invitationKeyNextCheckMs) {
        setTimeout(() => { this.onInvitationKeyCheck(value); }, 1000);
        return;
      }
      this
        .$settingGateway
        .checkInvitationKeyToBranch(value)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ nextCheck, status, title }) => {
            if (nextCheck) {
              this.invitationKeyNextCheckMs = nextCheck;
              this.onInvitationKeyCheck(value);
            } else {
              if (status === 'completed') {
                this.invitationKeyExpiresTimestamp = 0;
                this.invitationKeyTitle = title;
                this.$settingGateway.deactivateInvitationKeyToBranch(value);
              }
              this.onInvitationKeyExpired();
            }
          });
        });
    },
    /**
     * @description Обработать копирование ключа приглашения в сеть.
     * @param {Promise}
     * @returns {void}
     */
    onInvitationKeyCopied(result) {
      result.then(
        () => { this.notifySuccess('Ключ успешно скопирован.'); },
        () => { this.notifyFail('Не удалось скопировать ключ.'); },
      );
    },
    /**
     * @description Обработать изменения состояния диалога ключа приглашения.
     * @param {boolean} value Открыто.
     * @returns {void}
     */
    onInvitationKeyDialogInput(value) {
      if (value !== true) {
        if (this.invitationKeyToBranch !== '') {
          this
            .$settingGateway
            .deactivateInvitationKeyToBranch(this.invitationKeyToBranch);
          this.invitationKeyToBranch = '';
        }
        clearInterval(this.invitationKeyNowIntervalId);
        this.invitationKeyExpiresTimestamp = 0;
        this.invitationKeyNextCheckMs = 0;
        this.invitationKeyNowIntervalId = null;
      }
    },
    /**
     * @description Обработать закрытие диалога информации о ключе приглашения.
     * @returns {void}
     */
    onInvitationKeyDialogOk() {
      this.hasInvitationKeyTitleDialog = false;
    },
    /**
     * @description Обработать истечение ключа приглашения в сеть.
     * @returns {void}
     */
    onInvitationKeyExpired() {
      this.invitationKeyNextCheckMs = 0;
      this.invitationKeyToBranch = '';
    },
    /**
     * @description Обработать создание ключа для приглашения в сеть.
     * @returns {void}
     */
    onInviteToBranchCreateKey() {
      this
        .$settingGateway
        .createInvitationKeyToBranch()
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ expires_ms: expires, title, value }) => {
            const now = Date.now();
            this.notifySuccess(title);
            this.invitationKeyNowIntervalId = setInterval(
              () => { this.invitationKeyNowTimestamp = Date.now(); },
              1000,
            );
            this.invitationKeyNowTimestamp = now;
            this.invitationKeyNextCheckMs = now;
            this.invitationKeyExpiresTimestamp = expires;
            this.invitationKeyToBranch = value;
            this.isShownInviteToBranchDialog = false;
            this.onInvitationKeyCheck(value);
          });
        });
    },
    /**
     * @description Обработать открытие диалога приглашения в сеть.
     * @returns {void}
     */
    onInviteToBranchOpen() {
      this.isShownInviteToBranchDialog = true;
    },
    /**
     * @description Обработать открытие диалога присоединения к сети.
     * @returns {void}
     */
    onJoinToBranchOpen() {
      this.joinToBranchKey = '';
      this.isShownJoinToBranchDialog = true;
    },
    /**
     * @description Обработать отправку присоединения к сети.
     * @returns {void}
     */
    onJoinToBranchSubmit() {
      this
        .$settingGateway
        .joinToBranch(this.joinToBranchKey)
        .then((result) => {
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.isShownJoinToBranchDialog = false;
            this.isShownJoinToBranchSuccessDialog = true;
            this.joinToBranchKey = '';
          });
        });
    },
    /**
     * @description Обработать нажатие на успешное присоединение к сети.
     * @returns {void}
     */
    onJoinedToBranchClick() {
      this.isShownJoinToBranchSuccessDialog = false;
    },
    /**
     * @description Обработать готовность отправки.
     * @returns {void}
     */
    onReadySubmit() {
      const makeRequest = (branch) => new EditClinicSettingsRequest({
        ...branch,
        ...this.submitRequestOfSettings,
      });
      const onSuccess = (response) => {
        const { message } = response;
        const { title, body } = response.on_boarding_status;
        this.checkAndUpdate(title, body);
        this.notifySuccess(message);
        this.needsAccentToInstruction = true;
        this.violationCauses = {};
        this.submitTrigger = 0;
        this.renamingBranch = null;
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
        this.violationCauses = Object.fromEntries(
          error.causes.map((cause) => [cause.source, cause.details]),
        );
      };
      const items = Object.entries(this.submitRequestsForBranches).map(([id, branch]) => {
        const isAuth = this.authBranchId === id;
        const process = isAuth
          ? this.$settingGateway.sendUpdateClinicInfo(makeRequest(branch))
          : this.$settingGateway.editAdditionalBranch(id, makeRequest(branch));
        return process.then((result) => ({ id, isAuth, result }));
      });
      this.violationCauses = {};
      Promise.allSettled(items).then((results) => {
        let resultForAuth = Maybe.None();
        results.forEach(({ value: { id, isAuth, result } }) => {
          if (isAuth) {
            resultForAuth = result;
          }
          this.setViolationCausesForBranch(id, result);
        });
        resultForAuth.forEachLeft(onFail);
        const hasFailureForBranch = this.checkBranchesViolations();
        if (hasFailureForBranch) {
          this.notifyFail('Заполните недостающие данные');
        } else {
          resultForAuth.forEach(onSuccess);
        }
      });
    },
    /**
     * @description Обработать отправку данных филиала.
     * @param {string} id      Идентификатор филиала.
     * @param {object} request Данные настроек.
     * @param {number} trigger Триггер отправки.
     * @returns {void}
     */
    onSubmitBranch(id, trigger, request) {
      this.$set(this.submitRequestsForBranches, id, {
        ...request,
        branchName: this.branchNamesById[id],
      });
      this.$set(this.submitTriggersForBranches, id, trigger);
    },
    /**
     * @description Обработать отправку настроек.
     * @param {number} trigger Триггер отправки.
     * @param {object} request Данные отправки.
     * @returns {void}
     */
    onSubmitSettings(trigger, request) {
      this.submitRequestOfSettings = request;
      this.submitTriggerOfSettings = trigger;
    },
    /**
     * @description Получить настройки клиники.
     * @returns {void}
     */
    fetchSettings() {
      this
        .$settingGateway
        .viewClinicSettings(
          branchMapper,
          generalMapper,
        )
        .then((result) => result.fold(
          (error) => { this.notifyFail(error.getTitle()); },
          (data) => {
            this.actions = data.actions;
            this.additionalBranches = data.additionalBranches.map((branch) => ({
              description: branch.description,
              id: branch.id,
              isAuthenticated: branch.isAuthenticated,
              isRenamable: branch.isRenamable,
              props: collectBranchEditingProps(branch),
              title: branch.title,
            }));
            this.general = Maybe.Some(data.general);
            this.mainBranch = Maybe.Some(data.mainBranch);
            this.inactiveActions = data.inactiveActions;
            const failurePairs = (data.errors.causes || []).map((cause) => [
              cause.source,
              cause.details,
            ]);
            this.violationCauses = Object.assign(
              this.violationCauses,
              Object.fromEntries(failurePairs) || {},
            );
            this.checkBranchesViolations();
            this.branchNamesById = Object.fromEntries([
              ...data.additionalBranches.map(({ id, title }) => [id, title]),
              [data.mainBranch.id, data.mainBranch.title],
            ]);
            if (this.hasBranchSystem !== true) {
              this.openPanels = [0];
            }
          },
        ));
    },
    /**
     * @description Получить опции настроек.
     * @returns {void}
     */
    fetchSettingsOptions() {
      this.$suggestionGateway.viewClinicSettingsOptions().then((result) => {
        this.clinicSettingsOptions = result;
      });
    },
    /**
     * @description Сохранить и продолжить.
     * @returns {void}
     */
    async saveAndLeave() {
      this.$refs.formSetting.$el.requestSubmit();
      this.isShownDialogLeaveConfirm = false;
      this.actionAfterClosingDialogLeaveConfirm();
    },
    /**
     * @description Обработать изменение формы.
     * @param {boolean} isChanged Изменена ли форма.
     * @returns {void}
     */
    onChangeBranch(isChanged) {
      this.hasChangedBranch = isChanged;
    },
    /**
     * @description Обработать изменение формы.
     * @param {boolean} isChanged Изменена ли форма.
     * @returns {void}
     */
    onChangeSettings(isChanged) {
      this.hasChangedSettings = isChanged;
    },
    /**
     * @description Скролл к панели филиалов.
     * @returns {void}
     */
    onScrollToPanels() {
      this.$nextTick(() => {
        const firstInvalidField = document.querySelector('[aria-invalid="true"]');
        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
          firstInvalidField.focus({ preventScroll: true });
        }
      });
    },
    /**
     * @description Установить нарушения в запросе сохранения филиала.
     * @param {string} id     Идентификатор филиала.
     * @param {Maybe}  result Результат сохранения.
     * @returns {void}
     */
    setViolationCausesForBranch(id, result) {
      this.$set(this.violationCausesForBranches, id, result.fold(
        ({ causes }) => Object.fromEntries(
          causes.map((cause) => [cause.source, cause.details]),
        ),
        () => ({}),
      ));
    },
  },
  mixins: [
    onBoardingMixin,
    focusMixin,
    notifierMixin,
    settingInstructionMixin,
    preventLeaveWithoutConfirmMixin,
  ],
  name: 'ClinicPanelSettings',
  watch: {
    hasReadySubmit(value) {
      if (value) {
        this.$nextTick(() => { this.onReadySubmit(); });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.branch-rename {
  background: url("@/assets/img/pen-grey.svg") no-repeat center;
  font-size: 0;
  height: 22px;
  width: 22px;
}

.join-to-branch-dialog {
  &__input-secret {
    ::v-deep .input-secret__eye {
      top: 50%;
      transform: translateY(calc(-50% + 0.75rem))
    }
  }
}

.invite-to-branch-button {
  @include buttons.btn-blue(10rem, 3rem, 1rem);
  justify-self: center;
  margin-inline: auto;
}

.join-to-branch-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: min(100%, 20rem);
  & &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
  }
}

.settings {
  width: 100%;
  overflow-y: scroll;
  &__base-of-knowledge-button {
    background: none;
    border: none;
    color: var(--action-color);
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    cursor: pointer;
    padding: 0;
    &:hover {
      text-decoration: underline;
    }
  }
  & &__branch-form {
    margin-bottom: 15px;
  }
  & &__branch-mark {
    bottom: -1.4rem;
    color: var(--unfocus-color);
    font-size: .825rem;
    left: .125rem;
    position: absolute;
  }
  &__container {
    width: 1187px;
    padding: 38px 20px 200px 23px;
  }
  &__title {
    margin: 0;
  }
  &__description {
    display: flex;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    margin: 24px 0 45px 0;
  }
  &__form {
    background: var(--section-background-color);
    padding: 21px 31px;
    margin: 45px 0 15px 0;
  }
  &__text {
    margin: 20px 0 0 0;
    a:hover {
      text-decoration: none;
    }
  }
}

.settings-actions {
  display: flex;
  flex-flow: row wrap;
  gap: 20px;
  & &__button {
    @include buttons.btn-blue(174px, 48px, 16px);
  }
  & &__inactive-button {
    @include buttons.btn-transparent(174px, 48px, 16px);
  }
  & &__join-to-branch {
    min-width: 195px;
    width: min(100%, 205px);
  }
}

@media screen and (max-width: 1240px) {
  .settings {
    &__container {
      padding: 45px 20px 145px 20px;
      width: 100%;
    }
  }
}
@media screen and (max-width: 660px) {
  .settings {
    &__container {
      padding: 45px 10px 285px;
    }
    &__next-step {
      align-items: flex-end;
    }
  }
}
@media screen and (max-width: 480px) {
  .settings {
    &__form {
      padding: 20px 15px;
    }
  }
}
</style>
